/* ----------------- Types --------------- */
export interface Emotion {
	name: string;
	emoji: string;
	color: string;
	description: string;
}

/* ----------------- Constants --------------- */
export const DEFAULT_MAX_EMOTIONS = 5;

export const EMOTIONS: Record<string, Emotion> = {
	admiration: {
		name: "Admiration",
		emoji: "👏",
		color: "#FFD700",
		description: "Feeling of respect and warm approval",
	},
	adoration: {
		name: "Adoration",
		emoji: "😍",
		color: "#FF69B4",
		description: "Deep love and worship",
	},
	aestheticAppreciation: {
		name: "Aesthetic Appreciation",
		emoji: "🎨",
		color: "#DDA0DD",
		description: "Appreciation of beauty",
	},
	amusement: {
		name: "Amusement",
		emoji: "😂",
		color: "#98FB98",
		description: "Finding something funny or entertaining",
	},
	anger: {
		name: "Anger",
		emoji: "😠",
		color: "#FF4500",
		description: "Strong feeling of annoyance or displeasure",
	},
	annoyance: {
		name: "Annoyance",
		emoji: "🙄",
		color: "#FFA07A",
		description: "Slight anger or irritation",
	},
	anxiety: {
		name: "Anxiety",
		emoji: "😰",
		color: "#B8860B",
		description: "Worry or unease",
	},
	awe: {
		name: "Awe",
		emoji: "😲",
		color: "#9370DB",
		description: "Feeling of wonder or amazement",
	},
	awkwardness: {
		name: "Awkwardness",
		emoji: "😬",
		color: "#D3D3D3",
		description: "Feeling of discomfort or unease",
	},
	boredom: {
		name: "Boredom",
		emoji: "😑",
		color: "#A9A9A9",
		description: "Feeling of tedium or disinterest",
	},
	calmness: {
		name: "Calmness",
		emoji: "😌",
		color: "#E0FFFF",
		description: "State of tranquility or peace",
	},
	concentration: {
		name: "Concentration",
		emoji: "🤔",
		color: "#87CEEB",
		description: "Deep mental focus",
	},
	confusion: {
		name: "Confusion",
		emoji: "😕",
		color: "#DEB887",
		description: "Lack of understanding or clarity",
	},
	contemplation: {
		name: "Contemplation",
		emoji: "🧐",
		color: "#BDB76B",
		description: "Deep thoughtful observation",
	},
	contempt: {
		name: "Contempt",
		emoji: "😏",
		color: "#8B4513",
		description: "Feeling that something is worthless or beneath consideration",
	},
	contentment: {
		name: "Contentment",
		emoji: "😊",
		color: "#98FB98",
		description: "State of peaceful happiness",
	},
	craving: {
		name: "Craving",
		emoji: "🤤",
		color: "#FF8C00",
		description: "Powerful desire for something",
	},
	desire: {
		name: "Desire",
		emoji: "💘",
		color: "#FF69B4",
		description: "Strong feeling of wanting",
	},
	determination: {
		name: "Determination",
		emoji: "💪",
		color: "#4169E1",
		description: "Firm resolution or decision",
	},
	disappointment: {
		name: "Disappointment",
		emoji: "😞",
		color: "#778899",
		description: "Sadness from unfulfilled expectations",
	},
	disapproval: {
		name: "Disapproval",
		emoji: "👎",
		color: "#CD5C5C",
		description: "Having an unfavorable opinion",
	},
	disgust: {
		name: "Disgust",
		emoji: "🤢",
		color: "#9ACD32",
		description: "Strong aversion or repulsion",
	},
	distress: {
		name: "Distress",
		emoji: "😩",
		color: "#B22222",
		description: "Extreme anxiety or suffering",
	},
	doubt: {
		name: "Doubt",
		emoji: "🤨",
		color: "#DAA520",
		description: "Feeling of uncertainty",
	},
	ecstasy: {
		name: "Ecstasy",
		emoji: "🤩",
		color: "#FF1493",
		description: "Overwhelming joy or delight",
	},
	embarrassment: {
		name: "Embarrassment",
		emoji: "😳",
		color: "#FFB6C1",
		description: "Self-conscious discomfort",
	},
	empathicPain: {
		name: "Empathic Pain",
		emoji: "😥",
		color: "#4682B4",
		description: "Sharing others' suffering",
	},
	enthusiasm: {
		name: "Enthusiasm",
		emoji: "😃",
		color: "#FFA500",
		description: "Intense and eager enjoyment",
	},
	entrancement: {
		name: "Entrancement",
		emoji: "✨",
		color: "#9370DB",
		description: "State of rapturous delight",
	},
	envy: {
		name: "Envy",
		emoji: "😒",
		color: "#228B22",
		description: "Desire to have what others have",
	},
	excitement: {
		name: "Excitement",
		emoji: "😆",
		color: "#FF4500",
		description: "Feeling of great enthusiasm",
	},
	fear: {
		name: "Fear",
		emoji: "😱",
		color: "#800000",
		description: "Feeling of being afraid",
	},
	gratitude: {
		name: "Gratitude",
		emoji: "🙏",
		color: "#DDA0DD",
		description: "Feeling of thankfulness",
	},
	guilt: {
		name: "Guilt",
		emoji: "😔",
		color: "#696969",
		description: "Feeling of having done wrong",
	},
	horror: {
		name: "Horror",
		emoji: "😨",
		color: "#8B0000",
		description: "Intense fear or disgust",
	},
	interest: {
		name: "Interest",
		emoji: "👀",
		color: "#87CEEB",
		description: "Feeling of curiosity or engagement",
	},
	joy: {
		name: "Joy",
		emoji: "😁",
		color: "#FFD700",
		description: "Feeling of great pleasure",
	},
	love: {
		name: "Love",
		emoji: "❤️",
		color: "#FF69B4",
		description: "Deep affection or attachment",
	},
	nostalgia: {
		name: "Nostalgia",
		emoji: "🥲",
		color: "#DEB887",
		description: "Sentimental longing for the past",
	},
	pain: {
		name: "Pain",
		emoji: "😖",
		color: "#DC143C",
		description: "Physical or emotional suffering",
	},
	pride: {
		name: "Pride",
		emoji: "😎",
		color: "#9370DB",
		description: "Feeling of satisfaction in achievements",
	},
	realization: {
		name: "Realization",
		emoji: "💡",
		color: "#FFD700",
		description: "Sudden understanding or awareness",
	},
	relief: {
		name: "Relief",
		emoji: "😅",
		color: "#98FB98",
		description: "Feeling of anxiety or pain removal",
	},
	romance: {
		name: "Romance",
		emoji: "💕",
		color: "#FF69B4",
		description: "Feeling of love and attraction",
	},
	sadness: {
		name: "Sadness",
		emoji: "😢",
		color: "#4682B4",
		description: "Feeling of sorrow or unhappiness",
	},
	sarcasm: {
		name: "Sarcasm",
		emoji: "🙃",
		color: "#DDA0DD",
		description: "Mocking or ironic attitude",
	},
	satisfaction: {
		name: "Satisfaction",
		emoji: "🙂",
		color: "#98FB98",
		description: "Contentment with fulfillment",
	},
	shame: {
		name: "Shame",
		emoji: "😓",
		color: "#8B4513",
		description: "Feeling of humiliation or distress",
	},
	surpriseNegative: {
		name: "Surprise (negative)",
		emoji: "😧",
		color: "#CD853F",
		description: "Unexpected unpleasant feeling",
	},
	surprisePositive: {
		name: "Surprise (positive)",
		emoji: "😮",
		color: "#FFB6C1",
		description: "Unexpected pleasant feeling",
	},
	sympathy: {
		name: "Sympathy",
		emoji: "🤗",
		color: "#DDA0DD",
		description: "Feeling of compassion for others",
	},
	tiredness: {
		name: "Tiredness",
		emoji: "😴",
		color: "#778899",
		description: "Feeling of fatigue or exhaustion",
	},
	triumph: {
		name: "Triumph",
		emoji: "🏆",
		color: "#FFD700",
		description: "Feeling of great achievement",
	},
};

/* ----------------- Helpers --------------- */
export const getEmotionByName = (name: string): Emotion | undefined => {
	return EMOTIONS[name.toLowerCase()];
};

export const getAllEmotions = (): Emotion[] => {
	return Object.values(EMOTIONS);
};
